import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { api } from '../lib/api'
import type { RingDismissal } from '../lib/callRules'
import { useT, type MessageKey } from '../lib/i18n'
import { useCallStore } from '../stores/callStore'
import { useChatStore } from '../stores/chatStore'
import { Spinner } from '../components/Spinner'

type RouteState = 'loading' | 'ringing' | 'ended' | 'unavailable'

/** What the ring a sibling device took says here (W7): the frame's `reason`, worded. */
const DISMISSED_COPY: Record<RingDismissal['reason'], { title: MessageKey; detail: MessageKey }> = {
  answered: { title: 'call.answeredElsewhere', detail: 'call.answeredElsewhereDetail' },
  declined: { title: 'call.declinedElsewhere', detail: 'call.declinedElsewhereDetail' },
}

/**
 * The page under a ring that is over — a call that ended, one no longer ringing, or one a sibling
 * device answered or declined: one surface, so the words are the only thing that differs.
 */
function RingOver({ title, detail }: { title: string; detail: string }) {
  const t = useT()
  const navigate = useNavigate()
  return (
    <div className="app-shell items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-title font-semibold text-ink">{title}</h1>
      <p className="max-w-[24rem] text-ui text-muted">{detail}</p>
      <button
        type="button"
        onClick={() => void navigate('/', { replace: true })}
        className="mt-1 min-h-11 rounded-full bg-accent px-6 text-ui font-semibold press text-on-accent hover:bg-accent-strong"
      >
        {t('call.backToChats')}
      </button>
    </div>
  )
}

/**
 * Push-notification target (`/call/:callId`). The notification's `navigate` URL lands here, the
 * live call is fetched, and the ring overlay takes over (docs/CONTRACT.md §9).
 *
 * The store is the source of truth for "this call is ringing": a ring set by the live socket, by a
 * native cold start, or by the fetch below all land in `callStore.incoming`, and the overlay reads
 * the same store. Deriving that instead of latching it once is load-bearing — measured: the route
 * used to set a local `state = 'ringing'` on its first pass and never look at the store again, so
 * when `incoming` was cleared (a `call.end`, or the duplicate native delivery) the page stayed on
 * the "ringing" branch with nothing to render — a pure-black screen until a manual reload.
 */
export function IncomingCallRoute() {
  const t = useT()
  const { callId = '' } = useParams<{ callId: string }>()
  const navigate = useNavigate()
  const incoming = useCallStore((state) => state.incoming)
  const dismissedRings = useCallStore((state) => state.dismissedRings)
  const activeCallId = useCallStore((state) => state.active?.call.id ?? null)
  const [state, setState] = useState<RouteState>('loading')

  const ringing = incoming?.call.id === callId
  // W7: a sibling device answered or declined this call. The store brought the ring down, and the
  // fetch below must not raise it again: an empty `incoming` cannot say "taken down" rather than
  // "not arrived yet". A live ring outranks the record, because the api can ring this member into
  // the call again after a decline (`POST /api/calls/:callId/ring`).
  const dismissed: RingDismissal['reason'] | null = dismissedRings[callId] ?? null

  useEffect(() => {
    // Already ringing (the socket's `call.ring`, or a previous pass of this effect): nothing to do.
    if (ringing) return
    // A sibling took the call: the ring this page was showing is over, so it is not fetched back.
    if (dismissed !== null) return
    let mounted = true
    setState('loading')
    api
      .call(callId)
      .then(({ call, participants }) => {
        if (!mounted) return
        if (call.endedAt !== null) {
          setState('ended')
          return
        }
        // `GET /api/calls/:callId` reports who is in the SFU room right now. At ring time that is
        // nobody: the caller is still on their own prejoin screen, so `participants` is empty and
        // the incoming screen would fall back to "Someone". The chat's member list carries the name
        // instead (and survives a reload).
        const fromRoom = participants.find((participant) => participant.id === call.startedBy)
        const member = useChatStore
          .getState()
          .chats[call.chatId]?.members.find((candidate) => candidate.id === call.startedBy)
        useCallStore.getState().setIncoming({
          call,
          caller: fromRoom ?? member ?? null,
          fromPush: true,
        })
        setState('ringing')
      })
      .catch(() => {
        if (mounted) setState('unavailable')
      })
    return () => {
      mounted = false
    }
    // `ringing` and `dismissed` are dependencies so this re-runs — and re-populates the store — if
    // `incoming` is cleared while we are still on this route; a sibling's dismissal re-runs it only
    // to take the branch above, and the cleanup retires the fetch that may still be in flight.
  }, [callId, ringing, dismissed])

  // Once the call is accepted, land on the chat behind the overlay.
  useEffect(() => {
    if (activeCallId !== callId) return
    const chatId = useCallStore.getState().active?.call.chatId
    if (chatId) void navigate(`/chat/${chatId}`, { replace: true })
  }, [activeCallId, callId, navigate])

  if (ringing) {
    // The overlay itself is `IncomingCallRing`, rendered by RootLayout; this is the page under it.
    return <div className="app-shell" aria-hidden="true" />
  }

  if (dismissed !== null) {
    // Answered or declined on a sibling device: the ring is over here, and the page says which of
    // the two happened rather than leaving the reader with a ring that vanished.
    const copy = DISMISSED_COPY[dismissed]
    return <RingOver title={t(copy.title)} detail={t(copy.detail)} />
  }

  if (state === 'ringing' || state === 'loading') {
    // Either the first fetch is in flight, or the store was cleared and the effect above is
    // re-populating it. Never a blank page.
    return (
      <div className="app-shell items-center justify-center">
        <Spinner label={t('call.loadingCall')} />
      </div>
    )
  }

  return (
    <RingOver
      title={t(state === 'ended' ? 'call.ended' : 'call.unavailable')}
      detail={t(state === 'ended' ? 'call.everyoneHungUp' : 'call.noLongerRinging')}
    />
  )
}
